// ============================================================================
// SANDBOX HOOKS - API Integration for Sandbox Environments
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { SandboxEnvironment, SandboxProvider, Project } from '../types';

// ============================================================================
// GET SANDBOX STATUS
// ============================================================================

interface SandboxStatusResponse {
  sandboxEnvironment: SandboxEnvironment | null;
  ssoProfile?: string;
  consoleUrl?: string;
}

export const useSandboxStatus = (projectId: string) => {
  return useQuery<SandboxStatusResponse>({
    queryKey: ['sandbox-status', projectId],
    queryFn: async () => {
      const response = await api.get(`/api/projects/${projectId}/sandbox/status`);
      return response.data;
    },
    enabled: !!projectId,
    refetchInterval: (query) => {
      const env = query.state.data?.sandboxEnvironment;
      // Poll every 10s when provisioning or deploying
      if (env?.status === 'provisioning' || env?.status === 'decommissioning' || env?.deployStatus === 'deploying') {
        return 10000;
      }
      return false;
    },
  });
};

// ============================================================================
// REQUEST SANDBOX
// ============================================================================

interface RequestSandboxInput {
  provider: SandboxProvider;
  budgetLimit?: number;
  justification?: string;
  region?: string;
}

export const useRequestSandbox = (projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation<{ sandboxEnvironment: SandboxEnvironment }, Error, RequestSandboxInput>({
    mutationFn: async (input) => {
      const response = await api.post(`/api/projects/${projectId}/sandbox/request`, input);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sandbox-status', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });
};

// ============================================================================
// APPROVE SANDBOX (admin)
// ============================================================================

interface ApproveSandboxInput {
  projectId: string;
  budgetOverride?: number;
}

export const useApproveSandbox = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, ApproveSandboxInput>({
    mutationFn: async ({ projectId, budgetOverride }) => {
      await api.post(`/api/admin/sandbox/${projectId}/approve`, { budgetOverride });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sandbox-status', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['sandbox-queue'] });
    },
  });
};

// ============================================================================
// REJECT SANDBOX (admin)
// ============================================================================

interface RejectSandboxInput {
  projectId: string;
  reason?: string;
}

export const useRejectSandbox = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, RejectSandboxInput>({
    mutationFn: async ({ projectId, reason }) => {
      await api.post(`/api/admin/sandbox/${projectId}/reject`, { reason });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sandbox-status', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['sandbox-queue'] });
    },
  });
};

// ============================================================================
// SANDBOX APPROVAL QUEUE (admin)
// ============================================================================

interface SandboxQueueItem {
  project: Project;
  sandboxEnvironment: SandboxEnvironment;
}

export const useSandboxQueue = () => {
  return useQuery<SandboxQueueItem[]>({
    queryKey: ['sandbox-queue'],
    queryFn: async () => {
      const response = await api.get('/api/admin/sandbox/queue');
      return response.data.requests || [];
    },
    refetchInterval: 30000, // Poll every 30s
  });
};

// ============================================================================
// DECOMMISSION SANDBOX (admin)
// ============================================================================

export const useDecommissionSandbox = (projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, void>({
    mutationFn: async () => {
      await api.post(`/api/projects/${projectId}/sandbox/decommission`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sandbox-status', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });
};
